import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Pagination } from "antd";
import moment from "moment";

import { InterestAction, SidebarCountAction } from "../../store/actions";

import useScreenSize from "../../utils/usescreensize";

import ProfileTab from "../../components/tab/profiletab";
import DashboardCard from "../../components/dashboardcard/dashboardcard";
import { PrimaryButton, WhitePurpleButton } from "../../components/button";
import SortDropdown from "../../components/dropdown/sortdropdown";
import Cardloader from "../../components/loader/cardloader";
import Spacer from "../../components/spacer/spacer";
import UnderHeaderMenu from "../../components/underheadermenu";

import images from "../../assets";

import "./styles.css";

const Interests = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const screenSize = useScreenSize();
  const [searchParams, setSearchParams] = useSearchParams();

  const defaultTab = searchParams.get("tab") || "Pending";
  const defaultPage = parseInt(searchParams.get("page")) || 1;

  const { interests, isLoading } = useSelector((state) => state.interests);
  const profileData = useSelector((state) => state.profile?.profile);

  const [state, setState] = useState({
    activeTab: defaultTab,
    sortOption: "desc",
  });
  const [pageNo, setPageNo] = useState(defaultPage);
  const [pageSize] = useState(8);

  useEffect(() => {
    dispatch(SidebarCountAction.updateSidebarCount({ table: "interest" }));
  }, []);

  useEffect(() => {
    fetchInterests();
  }, [state.activeTab, state.sortOption]);

  useEffect(() => {
    setSearchParams({ tab: state.activeTab, page: pageNo.toString() });
  }, [state.activeTab, pageNo]);

  useEffect(() => {
    const tabFromURL = searchParams.get("tab") || "Pending";
    const pageFromURL = parseInt(searchParams.get("page")) || 1;

    if (tabFromURL !== state.activeTab) {
      setState((prev) => ({ ...prev, activeTab: tabFromURL }));
    }

    if (pageFromURL !== pageNo) {
      setPageNo(pageFromURL);
    }
  }, [searchParams]);

  const fetchInterests = () => {
    const payload = {
      filter: state?.activeTab,
      searchId: profileData?.username,
      sort: state?.sortOption,
    };
    dispatch(InterestAction.getInterests(payload));
  };

  const handleUpdateInterest = (id, status) => {
    const payload = {
      senderId: id,
      status,
    };
    dispatch(
      InterestAction.updateInterest(payload, () => {
        fetchInterests();
      })
    );
  };

  const tabItems = [
    { name: "Pending", title: "Pending" },
    { name: "My Interests", title: "My Interests" },
    { name: "Interested in Me", title: "Interested in Me" },
  ];

  const sortedInterests = useMemo(() => {
    if (!interests) return [];
    let sortedData = [...interests];

    if (state.sortOption === "desc") {
      sortedData.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
    } else {
      sortedData.sort((a, b) => new Date(a.updatedAt) - new Date(b.updatedAt));
    }

    return sortedData;
  }, [interests, state.sortOption]);

  const paginatedInterests = useMemo(() => {
    return sortedInterests.slice((pageNo - 1) * pageSize, pageNo * pageSize);
  }, [sortedInterests, pageNo, pageSize]);

  return (
    <div>
      {screenSize.width < 1023 && (
        <UnderHeaderMenu titlename="Interests">
          <div style={{ width: "110px" }}>
            <SortDropdown
              data={[
                { label: "Newest First", value: "desc" },
                { label: "Oldest First", value: "asc" },
              ]}
              onChange={(e) => setState({ ...state, sortOption: e })}
              value={state?.sortOption || undefined}
              placeholder="Sort by"
            />
          </div>
        </UnderHeaderMenu>
      )}
      <Spacer height="16px" />
      <div className="interests-section-top-part-container">
        <div></div>
        <div className="interests-section-header-container">
          {screenSize.width > 1023 && (
            <h1 className="interests-header">Interests</h1>
          )}
          <ProfileTab
            tabItems={tabItems}
            handleTab={(name) => {
              setState((prev) => ({ ...prev, activeTab: name }));
              setPageNo(1);
            }}
            activeTab={state?.activeTab}
            style={{
              background: "white",
              border: "1px solid #EAE6ED",
              width: "100%",
            }}
          />
        </div>
        {screenSize.width > 1023 && (
          <div className="interests-sort-container">
            <div style={{ width: "150px" }}>
              <SortDropdown
                data={[
                  { label: "Newest First", value: "desc" },
                  { label: "Oldest First", value: "asc" },
                ]}
                onChange={(e) => setState({ ...state, sortOption: e })}
                value={state?.sortOption || undefined}
                placeholder="Sort by"
              />
            </div>
          </div>
        )}
      </div>
      <Spacer height="12px" />
      <div className="interests-results-container">
        {isLoading ? (
          <Cardloader />
        ) : paginatedInterests.length > 0 ? (
          <>
            <div className="interests-results">
              {paginatedInterests.map((item, index) => (
                <DashboardCard
                  item={item}
                  index={index}
                  key={index}
                  redirectedfrom="interests"
                >
                  <div className="flex flex-col w-full gap-3">
                    {state?.activeTab === "Pending" && (
                      <div className="interests-card-text">
                        <p>Pending Since:</p>
                        <p>{moment(item?.updatedAt).fromNow()}</p>
                      </div>
                    )}
                    {state?.activeTab === "My Interests" && (
                      <div className="interests-card-text">
                        <p>Sent on:</p>
                        <p>
                          {moment(item?.updatedAt).format(
                            "YYYY-MM-DD, hh:mm A"
                          )}
                        </p>
                      </div>
                    )}
                    {state?.activeTab === "Interested in Me" && (
                      <div className="interests-card-text">
                        <p>Interest Received on:</p>
                        <p>
                          {moment(item?.updatedAt).calendar(null, {
                            sameDay: "[Today], h:mm A",
                            lastDay: "[Yesterday], h:mm A",
                            lastWeek: "dddd, h:mm A",
                            sameElse: "MMM DD, h:mm A",
                          })}
                        </p>
                      </div>
                    )}
                    {state?.activeTab === "Pending" ? (
                      <div className="interest-mobile-btn">
                        <PrimaryButton
                          style={{ background: "#F31260" }}
                          text="Decline"
                          onClick={() =>
                            handleUpdateInterest(item?.username, "rejected")
                          }
                        />
                        <PrimaryButton
                          style={{ background: "#17C964" }}
                          text="Accept"
                          onClick={() =>
                            handleUpdateInterest(item?.username, "accepted")
                          }
                        />
                      </div>
                    ) : (
                      <WhitePurpleButton
                        text="View Profile"
                        onClick={() =>
                          navigate(`/view-profile/${item?.username}`)
                        }
                      />
                    )}
                  </div>
                </DashboardCard>
              ))}
            </div>
            <Spacer height="36px" />
            <Pagination
              align="center"
              pageSize={pageSize}
              total={sortedInterests.length}
              current={pageNo}
              showSizeChanger={false}
              onChange={(page) => setPageNo(page)}
            />
            <Spacer height="24px" />
          </>
        ) : (
          <div className="flex flex-col justify-center items-center p-10 w-full gap-4">
            <img
              src={images.noActivityIcon}
              alt="no notifications"
              className="h-[160px] lg:h-full"
            />
            <div className="text-basePrimary font-bold text-[24px] lg:text-[32px]">
              {state?.activeTab === "Pending"
                ? "No Pending Interests"
                : state?.activeTab === "My Interests"
                ? "You Haven't Sent Any Interests"
                : "No One Has Shown Interest Yet"}
            </div>
            <p className="text-[14px] text-center max-w-[500px]">
              {state?.activeTab === "Pending"
                ? "When someone shows interest, you’ll see it here."
                : state?.activeTab === "My Interests"
                ? "Found someone special? Let them know!"
                : "Your perfect match may be just around the corner."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Interests;
